// CameraDashboardPage - 2ECOMP Fleet Management
// Main camera monitoring dashboard

import React, {
 useState, useCallback, useMemo
} from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Badge,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
  Zoom,
  CircularProgress,
  Alert,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ListIcon from '@mui/icons-material/List';
import SettingsIcon from '@mui/icons-material/Settings';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CameraGrid from '../components/CameraGrid';
import CameraCard from '../components/CameraCard';
import CameraIndicator from '../components/CameraIndicator';
import { useCameraStatus } from '../hooks/useCameraStatus';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  height: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
}));

const Header = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  borderRadius: 0,
  zIndex: theme.zIndex.appBar,
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('md')]: {
    flexWrap: 'nowrap',
  },
}));

const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  overflow: 'hidden',
}));

const Sidebar = styled(Paper)(({ theme, open }) => ({
  width: open ? 280 : 0,
  minWidth: open ? 280 : 0,
  transition: theme.transitions.create(['width', 'min-width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  borderRadius: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
}));

const DeviceListItem = styled(ListItemButton)(({ theme, active }) => ({
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.5, 1),
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
    },
  },
}));

const LiveBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.main,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

/**
 * CameraDashboardPage - Main camera monitoring interface
 */
const CameraDashboardPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Redux state
  const devicesMap = useSelector((state) => state.devices.items);
  const devices = useMemo(() => Object.values(devicesMap || {}), [devicesMap]);
  const positions = useSelector((state) => state.session.positions);

  // Local state
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'active' | 'offline'
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [focusedDevice, setFocusedDevice] = useState(null);

  // Camera status hook for all devices
  const { streams, getDevicesWithStreams, refresh, isLoading: statusLoading } = useCameraStatus();

  // Get devices with active streams
  const devicesWithStreams = useMemo(() => {
    const streamDevices = getDevicesWithStreams();
    return new Map(streamDevices.map(d => [d.deviceId, d.channels]));
  }, [getDevicesWithStreams]);

  // Filter devices
  const filteredDevices = useMemo(() => {
    let filtered = devices;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        d.name?.toLowerCase().includes(query) ||
        d.uniqueId?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (filterMode === 'active') {
      filtered = filtered.filter(d => devicesWithStreams.has(String(d.id)));
    } else if (filterMode === 'offline') {
      filtered = filtered.filter(d => !devicesWithStreams.has(String(d.id)));
    }

    return filtered;
  }, [devices, searchQuery, filterMode, devicesWithStreams]);

  // Devices to display in grid
  const gridDevices = useMemo(() => {
    if (selectedDevices.length > 0) {
      return selectedDevices.map(id => devices.find(d => d.id === id)).filter(Boolean);
    }
    // Default: show devices with active streams first
    return [...filteredDevices].sort((a, b) => {
      const aActive = devicesWithStreams.has(String(a.id)) ? 1 : 0;
      const bActive = devicesWithStreams.has(String(b.id)) ? 1 : 0;
      return bActive - aActive;
    }).slice(0, 9);
  }, [selectedDevices, filteredDevices, devices, devicesWithStreams]);

  // Stats
  const stats = useMemo(() => ({
    total: devices.length,
    active: devicesWithStreams.size,
    streamCount: streams.length,
  }), [devices.length, devicesWithStreams.size, streams.length]);

  // Handle device selection
  const handleDeviceSelect = useCallback((device) => {
    setSelectedDevices(prev => {
      if (prev.includes(device.id)) {
        return prev.filter(id => id !== device.id);
      }
      if (prev.length < 9) {
        return [...prev, device.id];
      }
      return prev;
    });
  }, []);

  // Handle device double-click for focus
  const handleDeviceFocus = useCallback((device) => {
    setFocusedDevice(device);
  }, []);

  // Handle filter mode change
  const handleFilterChange = useCallback((event, newFilter) => {
    if (newFilter !== null) {
      setFilterMode(newFilter);
    }
  }, []);

  // Handle view mode change
  const handleViewChange = useCallback((event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  }, []);

  // Clear selection
  const handleClearSelection = useCallback(() => {
    setSelectedDevices([]);
  }, []);

  // Navigate to settings
  const handleOpenSettings = useCallback((device) => {
    navigate(`/camera/settings/${device?.id || ''}`);
  }, [navigate]);

  // Check if device has active camera
  const isDeviceActive = useCallback((deviceId) => {
    return devicesWithStreams.has(String(deviceId));
  }, [devicesWithStreams]);

  return (
    <PageContainer>
      {/* Header */}
      <Header elevation={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={() => setSidebarOpen(!sidebarOpen)}>
            <FilterListIcon />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Câmeras
          </Typography>

          <Stack direction="row" spacing={1}>
            <Chip
              icon={<VideocamIcon />}
              label={`${stats.active} ao vivo`}
              color="success"
              size="small"
            />
            {!isMobile && (
              <Chip
                icon={<FiberManualRecordIcon />}
                label={`${stats.streamCount} streams`}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            size="small"
          >
            <ToggleButton value="grid">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list">
              <ListIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Atualizar">
            <IconButton onClick={refresh} disabled={statusLoading}>
              {statusLoading ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Configurações">
            <IconButton onClick={() => handleOpenSettings()}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Header>

      <MainContent>
        {/* Sidebar — Drawer on mobile, inline on desktop */}
        {(() => {
          const sidebarContent = (
            <>
              {/* Search and Filters */}
              <Box sx={{ p: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar veículo..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <ToggleButtonGroup
                  value={filterMode}
                  exclusive
                  onChange={handleFilterChange}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="all">
                    Todos ({devices.length})
                  </ToggleButton>
                  <ToggleButton value="active">
                    Ativos ({stats.active})
                  </ToggleButton>
                  <ToggleButton value="offline">
                    Offline ({devices.length - stats.active})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Divider />

              {/* Selection Info */}
              {selectedDevices.length > 0 && (
                <>
                  <Box sx={{ p: 1.5, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" color="primary">
                        {selectedDevices.length} selecionado(s)
                      </Typography>
                      <IconButton size="small" onClick={handleClearSelection}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Divider />
                </>
              )}

              {/* Device List */}
              <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
                {filteredDevices.map((device) => {
                  const isActive = isDeviceActive(device.id);
                  const isSelected = selectedDevices.includes(device.id);
                  const channels = devicesWithStreams.get(String(device.id)) || [];

                  return (
                    <DeviceListItem
                      key={device.id}
                      selected={isSelected}
                      onClick={() => handleDeviceSelect(device)}
                      onDoubleClick={() => handleDeviceFocus(device)}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {isActive ? (
                          <LiveBadge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            variant="dot"
                          >
                            <VideocamIcon color="success" />
                          </LiveBadge>
                        ) : (
                          <VideocamOffIcon color="disabled" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={device.name}
                        secondary={isActive ? `${channels.length} canal(is) ativo(s)` : 'Offline'}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: isSelected ? 600 : 400,
                          noWrap: true,
                        }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          color: isActive ? 'success.main' : 'text.disabled',
                        }}
                      />
                      {isActive && (
                        <ListItemSecondaryAction>
                          <Chip
                            label={channels.length}
                            size="small"
                            color="success"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </ListItemSecondaryAction>
                      )}
                    </DeviceListItem>
                  );
                })}

                {filteredDevices.length === 0 && (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum veículo encontrado
                    </Typography>
                  </Box>
                )}
              </List>
            </>
          );

          return isMobile ? (
            <Drawer
              anchor="left"
              open={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              PaperProps={{ sx: { width: 280 } }}
            >
              {sidebarContent}
            </Drawer>
          ) : (
            <Sidebar open={sidebarOpen} elevation={1}>
              {sidebarContent}
            </Sidebar>
          );
        })()}

        {/* Main Content Area */}
        <ContentArea>
          {/* Focused Device View */}
          {focusedDevice ? (
            <Box sx={{ height: '100%' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">{focusedDevice.name}</Typography>
                <IconButton onClick={() => setFocusedDevice(null)}>
                  <CloseIcon />
                </IconButton>
              </Stack>
              <CameraCard
                device={focusedDevice}
                autoPlay={true}
                showDeviceInfo={true}
                showChannelSelector={true}
                position={positions[focusedDevice.id]}
                onDeviceClick={() => navigate(`/position/${focusedDevice.id}`)}
                onOpenSettings={() => handleOpenSettings(focusedDevice)}
                sx={{ height: 'calc(100% - 60px)' }}
              />
            </Box>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <CameraGrid
              devices={gridDevices}
              initialLayout={isMobile ? 'single' : 'quad'}
              showToolbar={!isMobile}
              autoPlay={false}
              positions={positions}
              onDeviceClick={(device) => navigate(`/position/${device.id}`)}
              onFullscreen={handleDeviceFocus}
            />
          ) : (
            /* List View */
            <Grid container spacing={2}>
              {gridDevices.map((device) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={device.id}>
                  <CameraCard
                    device={device}
                    autoPlay={false}
                    showDeviceInfo={true}
                    showChannelSelector={true}
                    compact={true}
                    position={positions[device.id]}
                    onDeviceClick={() => navigate(`/position/${device.id}`)}
                    onOpenFullscreen={handleDeviceFocus}
                    onOpenSettings={() => handleOpenSettings(device)}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Empty State */}
          {gridDevices.length === 0 && !focusedDevice && (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VideocamOffIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhuma câmera selecionada
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {isMobile
                  ? 'Toque no filtro para selecionar veículos'
                  : 'Selecione veículos na lista à esquerda para visualizar'}
              </Typography>
            </Box>
          )}
        </ContentArea>
      </MainContent>

      {/* Fullscreen FAB */}
      <Zoom in={selectedDevices.length > 0}>
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))', right: 24 }}
          onClick={() => {
            if (selectedDevices.length === 1) {
              const device = devices.find(d => d.id === selectedDevices[0]);
              if (device) handleDeviceFocus(device);
            }
          }}
        >
          <FullscreenIcon />
        </Fab>
      </Zoom>
    </PageContainer>
  );
};

export default CameraDashboardPage;
